import { useEffect, useRef, useState } from "react"
import { ArrowUpRight, Menu, X, type LucideIcon } from "lucide-react"
import { Link, useLocation } from "react-router"

import {
  ArrowIcon,
  ButtonLink,
  NavPillLink,
  buttonClasses,
  navPillClasses,
} from "~/components/ui/button"
import { capture } from "~/client/posthog.client"
import { cn } from "~/lib/utils"
import { AUDIENCES_NAV } from "~/data/audiences-nav"
import { IconChip } from "~/components/landing/SectionKit"

// Top nav, Forest DS dark navbar (Figma "Landing Page — Internal Assets",
// Navbar 8:625): forest band, logo left, pill nav links centred, then the
// "Log in" secondary + "Book a meeting" primary on the right.
// Pill list: Products / Solutions / Blog / About / Career, then the
// external "Docs" link last. Career is the /jobs posting. FAQ left the menu
// on 2026-11-04: its questions now sit on /about (/faq redirects there).
const NAV: { label: string; href: string; external?: boolean }[] = [
  { label: "Products", href: "/#insights" },
  { label: "Solutions", href: "/solutions" },
  { label: "Blog", href: "/blog" },
  { label: "About", href: "/about" },
  { label: "Career", href: "/jobs" },
  { label: "Docs", href: "https://console.pine59.com/docs", external: true },
]

const CONTACT_HREF = "/get-started"

// Seven pills overflow the 1024px bar at the DS pill's px-5 (they ran into
// the logo once Career was added), so they sit a little tighter until xl,
// where there is room for the full padding again.
const PILL_FIT = "px-3.5 xl:px-5"

// On the landing page the "Product" pill lights up while one of the product
// sections is in view; it also scrolls there on click (href above).
const PRODUCT_SECTIONS = ["insights", "builders"]

// Which nav pill is "current" — a route match off the landing page, and a
// scroll-spy for "Product" on it.
function useActiveNav(): string | null {
  const { pathname } = useLocation()
  const [productInView, setProductInView] = useState(false)

  useEffect(() => {
    if (pathname !== "/") {
      setProductInView(false)
      return
    }
    const sections = PRODUCT_SECTIONS.map((id) =>
      document.getElementById(id),
    ).filter((el): el is HTMLElement => el != null)
    if (sections.length === 0) return

    const visible = new Set<Element>()
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) visible.add(e.target)
          else visible.delete(e.target)
        }
        setProductInView(visible.size > 0)
      },
      // only count a section once it's crossed into the middle of the viewport
      { rootMargin: "-30% 0px -40% 0px" },
    )
    sections.forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [pathname])

  if (pathname === "/") return productInView ? "Products" : null
  if (pathname.startsWith("/solutions")) return "Solutions"
  if (pathname.startsWith("/blog")) return "Blog"
  if (pathname.startsWith("/about")) return "About"
  if (pathname.startsWith("/jobs")) return "Career"
  return null
}

// The two products a customer logs into. Uses a native <details> disclosure so
// it works without client JS and stays SSR-safe / accessible.
const LOGINS = [
  {
    label: "Insights",
    hint: "No code needed",
    href: "https://app.pine59.com",
    event: "insights",
  },
  {
    label: "Console / API",
    hint: "For data teams",
    href: "https://console.pine59.com/login",
    event: "console",
  },
]

// One column of the Solutions mega menu ("Who it's for"): icon and name only,
// no description (the audience taglines live on the landing cards and
// /solutions). The source array (AUDIENCES_NAV) carries id/icon/name/href.
type NavColumnItem = {
  id: string
  icon: LucideIcon
  name: string
  href: string
}

function NavColumn({
  title,
  items,
  onSelect,
  bordered,
  columns,
}: {
  title?: string
  items: NavColumnItem[]
  onSelect: () => void
  bordered?: boolean
  // lay the items out in this many columns (a wide, horizontal menu) instead
  // of one tall list
  columns?: 2 | 3
}) {
  return (
    <div
      className={cn(
        "flex flex-col",
        bordered && "-ml-4 border-l border-line pl-4",
      )}
    >
      {title ? (
        <p className="px-2 pb-2 font-flex text-[11px] font-medium uppercase tracking-[0.14em] text-mute">
          {title}
        </p>
      ) : null}
      <div
        className={cn(
          columns === 3 && "grid grid-cols-3 gap-x-2 gap-y-1",
          columns === 2 && "grid grid-cols-2 gap-x-2 gap-y-1",
          !columns && "flex flex-col",
        )}
      >
        {items.map((item) => {
          const external = item.href.startsWith("http")
          const content = (
            <>
              <IconChip icon={item.icon} className="h-8 w-8" />
              <span className="flex items-center gap-1.5 font-sans text-[14px] font-medium leading-snug text-forest">
                {item.name}
                {external ? (
                  <ArrowUpRight
                    className="h-3 w-3 shrink-0 text-mute"
                    strokeWidth={2}
                  />
                ) : null}
              </span>
            </>
          )
          const className =
            "flex items-center gap-3 rounded-lg p-3 transition-colors hover:bg-cream"
          return external ? (
            <a
              key={item.id}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onSelect}
              className={className}
            >
              {content}
            </a>
          ) : (
            <Link
              key={item.id}
              to={item.href}
              onClick={onSelect}
              className={className}
            >
              {content}
            </Link>
          )
        })}
      </div>
    </div>
  )
}

// Desktop "Solutions" nav item: a click-to-open mega menu (native <details>,
// same disclosure pattern as "Log in") so a visitor sees the three audience
// areas before landing on /solutions. Opens on hover on pointer devices
// (Linear-style), and stays click / keyboard operable everywhere else.
function SolutionsMenu({
  detailsRef,
  selected,
  onSelect,
}: {
  detailsRef: React.RefObject<HTMLDetailsElement | null>
  selected: boolean
  onSelect: () => void
}) {
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )

  // There is no /solutions page to land on (it redirects to the first
  // audience), so the trigger only ever opens the menu. A mouse click keeps it
  // open rather than toggling it shut, since hovering has usually opened it
  // already. Keyboard (Enter / Space, e.detail === 0) falls through to the
  // native <details> toggle.
  const onSummaryClick = (e: React.MouseEvent) => {
    if (e.detail === 0) return
    e.preventDefault()
    if (detailsRef.current) detailsRef.current.open = true
  }

  const hoverOpen = (e: React.PointerEvent) => {
    if (e.pointerType !== "mouse") return
    clearTimeout(closeTimer.current)
    if (detailsRef.current) detailsRef.current.open = true
  }
  const hoverClose = (e: React.PointerEvent) => {
    if (e.pointerType !== "mouse") return
    clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => {
      if (detailsRef.current) detailsRef.current.open = false
    }, 140)
  }

  // The panel hangs off the pill (so the hover target stays contiguous), but
  // the pill sits left of centre and a wide panel centred on it would run off
  // the left edge at 1024px. On open, shift it by the distance from the pill's
  // centre to the page's centre, so it lands centred on screen.
  const summaryRef = useRef<HTMLElement>(null)
  const onToggle = () => {
    const d = detailsRef.current
    const pill = summaryRef.current
    if (!d?.open || !pill) return
    const r = pill.getBoundingClientRect()
    const shift =
      document.documentElement.clientWidth / 2 - (r.left + r.width / 2)
    d.style.setProperty("--menu-shift", `${shift}px`)
  }

  return (
    <details
      ref={detailsRef}
      onToggle={onToggle}
      onPointerEnter={hoverOpen}
      onPointerLeave={hoverClose}
      className="group relative [&_summary::-webkit-details-marker]:hidden"
    >
      <summary
        ref={summaryRef}
        onClick={onSummaryClick}
        className={cn(
          navPillClasses({ surface: "dark", selected }),
          "cursor-pointer list-none",
          PILL_FIT,
        )}
      >
        Solutions
      </summary>

      {/* pt-2 (not mt-2) keeps the hover target contiguous with the summary.
          A wide, horizontal panel: the six audiences in three columns, with
          the "Who it's for" label above them (no "All solutions" link: there
          is no hub page, each audience has its own). (It
          was a single tall column; v5 had also dropped "What it solves", the
          RetailNext card and the Products menu.) Centred on screen via
          --menu-shift, see onToggle. */}
      <div
        onPointerEnter={hoverOpen}
        onPointerLeave={hoverClose}
        className="absolute left-1/2 top-full z-30 w-[min(46rem,calc(100vw-2rem))] translate-x-[calc(-50%_+_var(--menu-shift,0px))] pt-2"
      >
        <div className="overflow-hidden rounded-[14px] border border-line bg-white p-5 shadow-[0px_16px_40px_rgba(0,41,15,0.16)]">
          <p className="mb-2 px-2 font-flex text-[11px] font-medium uppercase tracking-[0.14em] text-mute">
            Who it's for
          </p>
          <NavColumn columns={3} items={AUDIENCES_NAV} onSelect={onSelect} />
        </div>
      </div>
    </details>
  )
}

export function Header() {
  const loginRef = useRef<HTMLDetailsElement>(null)
  const menuRef = useRef<HTMLDetailsElement>(null)
  const solutionsRef = useRef<HTMLDetailsElement>(null)
  const active = useActiveNav()

  // On /get-started the primary CTA has nowhere to go — the whole page is its
  // destination. Demote it to a "current page" state that scrolls to the form
  // instead of reloading the route.
  const onContactPage =
    useLocation().pathname.replace(/\/+$/, "") === CONTACT_HREF
  const ctaHref = onContactPage ? "#request-form" : CONTACT_HREF
  const ctaVariant = onContactPage
    ? ("secondary" as const)
    : ("primary" as const)

  // Native <details> only toggle on their summary; close either one when the
  // user clicks (or tabs) anywhere outside it, or presses Escape.
  useEffect(() => {
    const refs = [loginRef, menuRef, solutionsRef]
    const close = (e: Event) => {
      for (const r of refs) {
        const el = r.current
        if (el?.open && !el.contains(e.target as Node)) el.open = false
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape")
        for (const r of refs) if (r.current) r.current.open = false
    }
    document.addEventListener("pointerdown", close)
    document.addEventListener("focusin", close)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("pointerdown", close)
      document.removeEventListener("focusin", close)
      document.removeEventListener("keydown", onKey)
    }
  }, [])

  const closeMenu = () => {
    if (menuRef.current) menuRef.current.open = false
  }
  const closeSolutions = () => {
    if (solutionsRef.current) solutionsRef.current.open = false
  }

  return (
    <header className="sticky top-0 z-50 bg-forest">
      {/* full-bleed with the same 20px gutter as the footer (Figma nav/footer
          both use p-[20px]) rather than the narrower u-container */}
      <div className="flex h-16 items-center gap-4 px-5 lg:h-[91px] lg:gap-6">
        {/* logo, left — flex-1 from lg so the desktop nav sits centred on the
            bar; just shrink-0 on mobile so it never gets squeezed by the
            actions */}
        <div className="flex shrink-0 items-center lg:flex-1">
          <Link
            to="/"
            aria-label="Pine59 home"
            className="flex h-11 shrink-0 items-center pr-2 lg:pr-3"
          >
            {/* symbol only on true mobile (< sm) so the nav actions fit; full
                wordmark from sm up. accent-vibrant (#ADC400) to match the
                wordmark's colour. A <picture> rather than two CSS-toggled
                <img>s: the browser fetches only the matching source, and
                React's SSR image preload skips <picture> children, so the
                unused logo never lands in the preload set. */}
            <picture>
              <source
                media="(min-width: 640px)"
                srcSet="/brand/logo-green.svg"
                width={176}
                height={50}
              />
              <img
                src="/brand/logo-symbol-lime.svg"
                alt=""
                width={102}
                height={42}
                className="h-5 w-auto sm:h-[18px]"
              />
            </picture>
          </Link>
        </div>

        {/* nav pills, centred in the middle of the bar (desktop) */}
        <div className="hidden shrink-0 lg:block">
          <nav className="flex items-center gap-1">
            {NAV.map((item) =>
              item.label === "Solutions" ? (
                <SolutionsMenu
                  key={item.label}
                  detailsRef={solutionsRef}
                  selected={active === "Solutions"}
                  onSelect={closeSolutions}
                />
              ) : (
                <NavPillLink
                  key={item.label}
                  href={item.href}
                  surface="dark"
                  className={PILL_FIT}
                  selected={item.label === active}
                  aria-current={item.label === active ? "page" : undefined}
                  {...(item.external
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                >
                  {item.label}
                  {item.external && (
                    <ArrowUpRight
                      className="ml-1 h-3.5 w-3.5 shrink-0"
                      strokeWidth={2}
                    />
                  )}
                </NavPillLink>
              ),
            )}
          </nav>
        </div>

        {/* actions, right — Book a meeting at every breakpoint (compact, no
            arrows, on mobile). Log in is desktop-only here: on mobile it
            moves into the hamburger panel instead, so the bar doesn't ask a
            phone visitor to log in before they've even read anything. */}
        <div className="flex flex-1 items-center justify-end gap-2 lg:gap-3">
          <details
            ref={loginRef}
            className="group hidden [&_summary::-webkit-details-marker]:hidden lg:relative lg:block"
          >
            <summary
              className={cn(
                buttonClasses({ variant: "secondary", surface: "dark" }),
                "cursor-pointer list-none gap-1 px-3 py-2 text-[13px] [&>svg]:hidden lg:gap-2.5 lg:p-4 lg:text-[16px] lg:[&>svg]:block",
              )}
            >
              Log in
              <ArrowIcon className="transition-transform duration-200 group-open:rotate-90" />
            </summary>
            {/* mobile: anchored to the header's right gutter, width-capped to
                the viewport. desktop: dropped under the Log in button. */}
            <div className="absolute right-5 top-full z-30 mt-2 w-[min(18rem,calc(100vw-2.5rem))] overflow-hidden rounded-[6px] border border-line bg-white shadow-[0px_12px_32px_rgba(0,41,15,0.14)] lg:right-0 lg:w-72">
              {LOGINS.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => capture("login_clicked", { product: l.event })}
                  className="flex items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-cream"
                >
                  <span className="font-sans text-[16px] font-medium text-forest">
                    {l.label}
                  </span>
                  <span className="whitespace-nowrap font-flex text-[11px] uppercase tracking-wide text-mute">
                    {l.hint}
                  </span>
                </a>
              ))}
            </div>
          </details>

          <ButtonLink
            href={ctaHref}
            variant={ctaVariant}
            surface={onContactPage ? "dark" : undefined}
            withArrow={onContactPage ? true : undefined}
            arrowDirection={onContactPage ? "down" : undefined}
            aria-current={onContactPage ? "page" : undefined}
            className="whitespace-nowrap px-3 py-2 text-[13px] [&>svg]:hidden lg:p-4 lg:text-[16px] lg:[&>svg]:block"
            // The conquest page makes this button glow on that page only
            // (NAV_CTA_GLOW in routes/foot-traffic-data-alternative.tsx).
            data-nav-cta=""
            onClick={
              onContactPage
                ? undefined
                : () => capture("contact_clicked", { location: "nav" })
            }
          >
            Book a meeting
          </ButtonLink>

          {/* hamburger — mobile only, nav links only */}
          <details
            ref={menuRef}
            className="group lg:hidden [&_summary::-webkit-details-marker]:hidden"
          >
            <summary
              aria-label="Menu"
              className="flex h-10 w-9 cursor-pointer list-none items-center justify-center text-cream transition-colors hover:text-chartreuse"
            >
              <Menu className="h-6 w-6 group-open:hidden" strokeWidth={1.75} />
              <X
                className="hidden h-6 w-6 group-open:block"
                strokeWidth={1.75}
              />
            </summary>

            {/* full-width panel dropping below the sticky bar */}
            <div className="absolute inset-x-0 top-full border-b border-moss/40 bg-forest shadow-[0px_12px_32px_rgba(0,41,15,0.4)]">
              <nav className="flex flex-col px-5 py-3">
                {NAV.map((item) => (
                  <div key={item.label}>
                    {/* Solutions has no page of its own, so on mobile it is
                        a label over its audience links, not a link. */}
                    {item.label === "Solutions" ? (
                      <p
                        className={
                          "py-2.5 font-sans text-[17px] font-medium " +
                          (item.label === active
                            ? "text-chartreuse"
                            : "text-cream")
                        }
                      >
                        {item.label}
                      </p>
                    ) : (
                      <a
                        href={item.href}
                        onClick={closeMenu}
                        aria-current={
                          item.label === active ? "page" : undefined
                        }
                        {...(item.external
                          ? { target: "_blank", rel: "noopener noreferrer" }
                          : {})}
                        className={
                          "block py-2.5 font-sans text-[17px] font-medium transition-colors hover:text-chartreuse " +
                          (item.label === active
                            ? "text-chartreuse"
                            : "text-cream")
                        }
                      >
                        {item.label}
                        {item.external && (
                          <ArrowUpRight
                            className="ml-1 inline h-4 w-4 align-[-2px]"
                            strokeWidth={2}
                          />
                        )}
                      </a>
                    )}
                    {item.label === "Solutions" && (
                      <div className="mb-1 ml-1 flex flex-col border-l border-moss/30 pl-3">
                        {AUDIENCES_NAV.map((a) => (
                          <a
                            key={a.id}
                            href={a.href}
                            onClick={closeMenu}
                            className="py-2 font-sans text-[15px] text-cream/75 transition-colors hover:text-chartreuse"
                          >
                            {a.name}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>

              {/* Log in — folded in here instead of its own top-level
                  button (see the note above the actions block). */}
              <div className="border-t border-moss/30 px-5 py-3">
                <p className="font-flex text-[11px] font-medium uppercase tracking-wide text-cream/50">
                  Log in
                </p>
                <div className="mt-1 flex flex-col">
                  {LOGINS.map((l) => (
                    <a
                      key={l.label}
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        capture("login_clicked", { product: l.event })
                        closeMenu()
                      }}
                      className="flex items-center justify-between gap-4 py-2.5 font-sans text-[17px] font-medium text-cream transition-colors hover:text-chartreuse"
                    >
                      {l.label}
                      <span className="whitespace-nowrap font-flex text-[11px] uppercase tracking-wide text-cream/50">
                        {l.hint}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </details>
        </div>
      </div>
    </header>
  )
}
